import { useI18n } from 'context/i18n';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { getTWSMagazineUrl } from 'utils/tws';
import { getDomainValue, isDomain } from 'utils/domain';
import { useLocale } from 'hooks/useLocale';

import { AccountFaceOutlineSVG } from 'components/iconsSVG/accountFaceOutline';
import { SadFaceSVG } from 'components/iconsSVG/sadFace';
import type { AutoString } from 'types/utils';

type ThankYouMessageType = AutoString<
  | 'notifyme'
  | 'appointment'
  | 'ewallet'
  | 'newsletter'
  | 'eventSuscribedUserNew'
  | 'eventSuscribedUserExists'
  | 'eventSuscribedAlready'
  | 'eventSuscribedFull'
  | 'eventUnsuscribed'
  | 'content'
  | 'reserve'
  | 'magazine'
  | `magazine-${string}`
>;

const icons = {
  face: <AccountFaceOutlineSVG width={50} height={50} />,
  socks: <img src="/assets-new/newsletter-subscription-socks.gif" alt="Subscription OK!" width={90} height={90} />,
  sad: <SadFaceSVG width={50} height={50} />,
  nnormal: <img src={'/assets-new/logo-nnormal.svg'} alt={'Nnormal Logo'} width={200} height={80} />,
  camperlab: <img src="/assets-new/svg/camperlab/camperlab-logo.svg" alt="CamperLab Logo" className="mb-4" height={70} />,
};

function getConfig({ type, language, href, t }: { type: ThankYouMessageType; language: string; href?: string; t: (parent: string, key: string) => string }) {
  const config: {
    [key in ThankYouMessageType]: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      subtitleHtml?: React.ReactNode;
      cta?: string;
      showCollections?: boolean;
      ctaHref?: string;
    };
  } = {
    notifyme: {
      icon: icons['face'],
      title: t('generico', 'thank.you.notifyme'),
      subtitle: t('generico', 'see.you.soon.notifyme'),
      cta: '',
      showCollections: false,
    },
    appointment: {
      icon: icons['face'],
      title: t('generico', 'thank.you'),
      subtitle: t('generico', 'see.you.soon'),
      cta: '',
      showCollections: false,
    },
    ewallet: {
      icon: icons['face'],
      title: t('generico', 'thank.you.ewallet'),
      subtitle: t('generico', 'see.you.soon.ewallet'),
      cta: '',
      showCollections: false,
    },
    newsletter: {
      icon: icons['socks'],
      title: t('generico', 'thank.you.newsletter'),
      subtitle: t('generico', 'see.you.soon.newsletter'),
      cta: t('generico', 'thank.you.cta'),
      showCollections: true,
    },
    eventSuscribedUserNew: {
      icon: icons[getDomainValue({ camper: 'face', nnormal: 'nnormal', camperlab: 'camperlab' })],
      title: t('generico', 'thank.you.event.suscribe.user.new'),
      subtitle: '',
      subtitleHtml: (
        <div className="mb-0 text-xs text-copy lg:mt-6" dangerouslySetInnerHTML={{ __html: t('generico', 'see.you.soon.event.suscribe.user.new') }}></div>
      ),
      cta: '',
      showCollections: false,
    },
    eventSuscribedUserExists: {
      icon: icons[getDomainValue({ camper: 'face', nnormal: 'nnormal', camperlab: 'camperlab' })],
      title: t('generico', 'thank.you.event.suscribe.user.exists'),
      subtitle: t('generico', 'see.you.soon.event.suscribe.user.exists'),
      cta: '',
      showCollections: false,
    },
    eventSuscribedAlready: {
      icon: icons[getDomainValue({ camper: 'face', nnormal: 'nnormal', camperlab: 'camperlab' })],
      title: t('generico', 'thank.you.event.suscribe.already'),
      subtitle: t('generico', 'see.you.soon.event.suscribe.already'),
      cta: '',
      showCollections: false,
    },
    eventSuscribedFull: {
      icon: icons[getDomainValue({ camper: 'sad', nnormal: 'nnormal', camperlab: 'camperlab' })],
      title: t('generico', 'thank.you.event.suscribe.full'),
      subtitle: t('generico', 'see.you.soon.event.suscribe.full'),
      cta: '',
      showCollections: false,
    },
    eventUnsuscribed: {
      icon: icons[getDomainValue({ camper: 'sad', nnormal: 'nnormal', camperlab: 'camperlab' })],
      title: t('generico', 'thank.you.event.unsuscribe.title'),
      subtitle: t('generico', 'see.you.soon.event.unsuscribe.subtitle'),
      cta: '',
      showCollections: false,
    },
    content: {
      icon: icons[getDomainValue({ camper: 'face', nnormal: 'nnormal', camperlab: 'camperlab' })],
      title: t('ficha.producto', 'modal.members.only.title.content'),
      subtitle: t('ficha.producto', 'modal.members.only.subtitle.content'),
      cta: t('ficha.producto', 'modal.members.only.cta.redirect'),
      ctaHref: href,
      showCollections: false,
    },
    reserve: {
      icon: icons[getDomainValue({ camper: 'face', nnormal: 'nnormal', camperlab: 'camperlab' })],
      title: t('generico', 'thank.you'),
      subtitleHtml: <p dangerouslySetInnerHTML={{ __html: t('generico', 'see.you.soon') }} />,
      showCollections: false,
    },
    magazine: {
      icon: icons['face'],
      title: t('generico', 'thank.you.magazine'),
      cta: t('generico', 'read.it.now'),
      showCollections: false,
    },
  };
  if (type.includes('magazine')) {
    const baseMagazine: { icon: React.ReactNode; title: string; cta: string; showCollections: boolean; ctaHref?: string } = {
      icon: icons['face'],
      title: t('generico', 'thank.you.magazine'),
      cta: t('generico', 'read.it.now'),
      showCollections: false,
    };
    const magazineType = type.split('-').pop();
    const ctaHref = getTWSMagazineUrl(magazineType, language);

    baseMagazine.ctaHref = ctaHref;
    config[type] = baseMagazine;
  }
  return config[type] ?? null;
}

type ThankYouMessageProps = {
  type: ThankYouMessageType;
  className?: string;
  href?: string;
};

export function ThankYouMessage({ type, className, href }: ThankYouMessageProps) {
  const {
    t,
    profileData: { emptyBagCollections },
  } = useI18n();
  const { language } = useLocale();

  const handleClickCollectionButton = (collectionName: string) => {
    dataLayerHandleEvent({
      event: 'select_enrichment_end_promotion',
      event_category: 'acquisition',
      event_detail_1: collectionName,
    });
  };

  const config = getConfig({ type, language, href, t });

  if (!config) {
    console.error('Unrecognized type in thank you message.');
    return null;
  }

  return (
    <div className={className}>
      <div className="lab:hidden">{config.icon}</div>
      <p className="text-base capitalize text-copy lab:mb-2 lab:text-sm lab:font-bold lab:uppercase lab:lg:text-xs">{config.title}</p>
      {config.subtitleHtml && config.subtitleHtml}
      {config.subtitle && <p className="mb-0 text-xs text-copy lab:text-sm lab:font-bold lg:mt-6 lab:lg:mt-0 lab:lg:text-xs">{config.subtitle}</p>}
      {config.ctaHref && (
        <a href={config.ctaHref} className="relative top-5 border border-dark p-2" target="_blank" rel="noreferrer">
          {config.cta}
        </a>
      )}
      {isDomain('camper') && config.showCollections && (
        <>
          <p className="mb-0 text-xs text-copy lg:mt-6">{config.cta}</p>
          <div className="my-2.5 flex flex-wrap justify-center">
            {emptyBagCollections?.map((item) => (
              <a
                key={`collectionButton-${item.title}`}
                href={item.url}
                onClick={() => handleClickCollectionButton(item.id)}
                className="m-[2%] h-10 w-[120px] border border-dark bg-neutral-inverted px-2.5 py-2.5 text-center text-base text-inverted hover:bg-clear hover:text-copy"
              >
                {item.title}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
